import { useEffect, useRef, useState } from "react";
import { apiClient } from "services/Api";

export const MIN_CONTENT_QUERY_LENGTH = 2;

// Free-text search in the content of the applications of a typology
// (GET /applications/<typology>/search/). Returns the hits by application id
// ({ match_count, matches }) so the list pages can filter their own rows;
// `hits` is null while there is no search (query shorter than 2 characters).
export const useContentSearch = (typology, query, statusFilter) => {
  const [hits, setHits] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const requestIdRef = useRef(0);
  const trimmed = (query ?? "").trim();

  useEffect(() => {
    // Any newer state (even an empty query) makes older responses stale.
    const requestId = ++requestIdRef.current;
    if (trimmed.length < MIN_CONTENT_QUERY_LENGTH) {
      setHits(null);
      setLoading(false);
      setError(false);
      return undefined;
    }

    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const params = new URLSearchParams({
          q: trimmed,
          status: statusFilter || "todos",
        });
        const { data } = await apiClient.get(
          `/applications/${typology}/search/?${params.toString()}`,
        );
        if (requestId !== requestIdRef.current) return;
        setHits(
          new Map((data.results ?? []).map((hit) => [hit.id, hit])),
        );
        setError(false);
      } catch {
        if (requestId !== requestIdRef.current) return;
        setHits(new Map());
        setError(true);
      } finally {
        if (requestId === requestIdRef.current) setLoading(false);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [typology, trimmed, statusFilter]);

  return { hits, loading, error, active: hits !== null || loading };
};
